import type { ComponentType } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { ComponentWithChildren } from '@polyai/common/types/helpers';

import type { formatPostFunction } from 'components/organisms/FunctionForm/utils';
import useFlows from 'hooks/useFlows/useFlows';
import useFunctions from 'hooks/useFunctions';
import useFunctionActions from 'hooks/useFunctions/useFunctionActions';
import useFlowDetailsStore from 'screens/FlowDetails/hooks/useFlowDetailsStore';

type OnNameChange = (name: string) => void;
type FunctionUpdate = ReturnType<typeof formatPostFunction>;

const FunctionSidebarContext = createContext<{
  onNameChange?: OnNameChange;
  setOnNameChange: (fn?: OnNameChange) => void;
  activeFunctionId?: string;
  setActiveFunctionId: (funcId?: string) => void;
}>(
  new Proxy({} as any, {
    get: () => {
      throw new Error('You are not within a FunctionSidebarContext');
    },
  }),
);

export const useFunctionSidebarContext = () => {
  const context = useContext(FunctionSidebarContext);
  const {
    activeFunctionId,
    setActiveFunctionId,
    setOnNameChange,
    onNameChange,
  } = context;

  const { setActiveSidebar, isSidebarOpen } = useFlowDetailsStore();

  const { allActiveFunctions, isLoadingFunctions } = useFunctions();
  const { postFunction } = useFunctionActions();
  const { flows, isLoadingFlows } = useFlows();
  const existingFunctionNames = useMemo(
    () =>
      allActiveFunctions
        .filter(f => f.function_id !== activeFunctionId)
        .map(f => f.name),
    [activeFunctionId, allActiveFunctions],
  );

  const openFunction = useCallback(
    (id: string, onChange?: OnNameChange) => {
      setActiveFunctionId(id);
      setOnNameChange(() => onChange);
      setActiveSidebar({
        type: 'function',
      });
    },
    [setActiveFunctionId, setOnNameChange, setActiveSidebar],
  );

  const closeFunction = useCallback(() => {
    setActiveFunctionId(undefined);
    setOnNameChange(undefined);
    setActiveSidebar(undefined);
  }, [setActiveFunctionId, setOnNameChange, setActiveSidebar]);

  const updateFunction = useCallback(
    (func: FunctionUpdate) => {
      postFunction(
        { func },
        {
          onSuccess: () => {
            onNameChange?.(func.name);
          },
        },
      );
    },
    [onNameChange, postFunction],
  );

  return {
    ...context,
    activeFunction: allActiveFunctions.find(
      f => f.function_id === activeFunctionId,
    ),
    existingFunctionNames,
    flows,
    isLoadingFlows,
    isLoadingFunctions,
    closeFunction,
    openFunction,
    updateFunction,
    isOpen: isSidebarOpen ? isSidebarOpen('function') : !!activeFunctionId,
  };
};

export const useFunctionSidebarContextStream = () => {
  const context = useContext(FunctionSidebarContext);
  const { setActiveFunctionId, setOnNameChange } = context;

  const { setActiveSidebar } = useFlowDetailsStore();

  const openFunction = useCallback(
    (id: string, onChange?: OnNameChange) => {
      setActiveFunctionId(id);
      setOnNameChange(() => onChange);
      setActiveSidebar({
        type: 'function',
      });
    },
    [setActiveFunctionId, setOnNameChange, setActiveSidebar],
  );

  const closeFunction = useCallback(() => {
    setActiveFunctionId(undefined);
    setOnNameChange(undefined);
    setActiveSidebar(undefined);
  }, [setActiveFunctionId, setOnNameChange, setActiveSidebar]);

  return {
    ...context,
    closeFunction,
    openFunction,
  };
};

export const FunctionSidebarContextProvider: ComponentWithChildren = ({
  children,
}) => {
  const [onNameChange, setOnNameChange] = useState<OnNameChange>();
  const [activeFunctionId, setActiveFunctionId] = useState<string>();

  return (
    <FunctionSidebarContext.Provider
      value={useMemo(
        () => ({
          onNameChange,
          setOnNameChange,
          activeFunctionId,
          setActiveFunctionId,
        }),
        [activeFunctionId, onNameChange],
      )}
    >
      {children}
    </FunctionSidebarContext.Provider>
  );
};

export const withFunctionSidebar =
  <T extends {}>(Component: ComponentType<T>) =>
  (props: T) => (
    <FunctionSidebarContextProvider>
      <Component {...props} />
    </FunctionSidebarContextProvider>
  );
